import type { ProposalBuilderData } from '../types';
import { calculateProposalNodeBuckets } from './proposalPublicGrandTotals';
import {
  computeProposalSendSummaryEconomics,
  getProposalDiscountAmount,
} from './proposalCalculations';
import { speciesTimberKitBillable } from './proposalSpeciesProduction';

export interface VersionSpeciesPriceRow {
  speciesName: string;
  billableTotal: number;
  isActive: boolean;
  isDefault?: boolean;
}

function sortSpeciesRows(rows: VersionSpeciesPriceRow[]): VersionSpeciesPriceRow[] {
  return [...rows].sort((a, b) => {
    if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
    if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
    return a.speciesName.localeCompare(b.speciesName, undefined, { sensitivity: 'base' });
  });
}

function enabledNodeSets(data: ProposalBuilderData) {
  return (data.nodeSets || []).filter((ns) => ns.enabled !== false);
}

function hasCustomMultiSpecies(data: ProposalBuilderData): boolean {
  return enabledNodeSets(data).some((ns) => ns.materials?.quoteMultipleSpecies);
}

function kitSpeciesNames(data: ProposalBuilderData): string[] {
  const offered = (data.offeredSpecies || []).filter(Boolean);
  if (offered.length > 0) return offered;
  return (data.kitPricing || []).map((kp) => kp.speciesName).filter(Boolean);
}

function computeKitSpeciesRows(data: ProposalBuilderData): VersionSpeciesPriceRow[] {
  const names = kitSpeciesNames(data);
  if (names.length === 0) return [];

  const active = (data.activeSpecies || '').trim();
  const nodeSets = data.nodeSets || [];
  const profitMargin = data.profitMargin ?? 40;

  return names.map((speciesName) => {
    const economics = computeProposalSendSummaryEconomics(nodeSets, profitMargin, data.globalCosts, {
      isKitProposal: true,
      kitPricing: data.kitPricing,
      activeSpecies: speciesName,
      discountAmount: getProposalDiscountAmount(data.discount),
    });
    return {
      speciesName,
      billableTotal: Math.round(economics.billableTotal),
      isActive: !!active && speciesName === active,
      isDefault: names[0] === speciesName,
    };
  });
}

function computeCustomMultiSpeciesRows(data: ProposalBuilderData): VersionSpeciesPriceRow[] {
  const enabled = enabledNodeSets(data);
  if (enabled.length === 0) return [];

  const discountAmount = getProposalDiscountAmount(data.discount);
  const applyDiscount = (total: number) => Math.max(0, total - discountAmount);

  const primarySpecies =
    enabled[0]?.materials?.timberList?.[0]?.species?.trim() || 'Default';

  const defaultTotal = enabled.reduce((sum, ns) => {
    const buckets = calculateProposalNodeBuckets(ns, data.profitMargin, data);
    return sum + buckets.total;
  }, 0);

  const alternateNames = new Set<string>();
  enabled.forEach((ns) => {
    (ns.materials?.alternateSpecies || []).forEach((s) => {
      if (s.speciesName) alternateNames.add(s.speciesName);
    });
  });

  const rows: VersionSpeciesPriceRow[] = [
    {
      speciesName: primarySpecies,
      billableTotal: Math.round(applyDiscount(defaultTotal)),
      isActive: !data.activeSpecies || data.activeSpecies === primarySpecies,
      isDefault: true,
    },
  ];

  alternateNames.forEach((speciesName) => {
    const speciesTotal = enabled.reduce((sum, ns) => {
      const buckets = calculateProposalNodeBuckets(ns, data.profitMargin, data);

      const timberKit = speciesTimberKitBillable(
        ns,
        speciesName,
        buckets.timberKit,
        data.profitMargin,
        ns.quantity || 1,
        ns.billablePrices?.speciesOptions,
      );
      return sum + buckets.design + timberKit + buckets.install;
    }, 0);

    rows.push({
      speciesName,
      billableTotal: Math.round(applyDiscount(speciesTotal)),
      isActive: data.activeSpecies === speciesName,
      isDefault: false,
    });
  });

  return rows;
}

function sumBillableDisplayFromProposal(data: ProposalBuilderData): number {
  const enabled = enabledNodeSets(data);
  const sum = enabled.reduce((s, ns) => s + (ns.billablePrices?.subtotal || 0), 0);
  const display = (data as ProposalBuilderData & { displayTotal?: number }).displayTotal;
  if (typeof display === 'number' && Number.isFinite(display)) {
    // displayTotal is saved post-discount — use as-is.
    return Math.round(display);
  }
  // billablePrices subtotals are pre-discount; subtract it here.
  return Math.round(Math.max(0, sum - getProposalDiscountAmount(data.discount)));
}

function computeSingleSpeciesRow(data: ProposalBuilderData): VersionSpeciesPriceRow {
  const enabled = enabledNodeSets(data);
  const billableTotal = sumBillableDisplayFromProposal(data);
  const primarySpecies =
    data.activeSpecies?.trim() ||
    enabled[0]?.materials?.timberList?.[0]?.species?.trim() ||
    kitSpeciesNames(data)[0] ||
    'Default';

  return {
    speciesName: primarySpecies,
    billableTotal,
    isActive: true,
    isDefault: true,
  };
}

/** Client price per offered species for a frozen proposal version snapshot. */
export function computeVersionSpeciesPricingRows(data: ProposalBuilderData): VersionSpeciesPriceRow[] {
  if (!data) return [];

  if (data.isKitProposal && data.kitPricing && data.kitPricing.length > 0) {
    const kitRows = computeKitSpeciesRows(data);
    if (kitRows.length > 0) return sortSpeciesRows(kitRows);
  }

  if (hasCustomMultiSpecies(data)) {
    const customRows = computeCustomMultiSpeciesRows(data);
    if (customRows.length > 1) return sortSpeciesRows(customRows);
  }

  return [computeSingleSpeciesRow(data)];
}

/** One-line summary for table tooltips. */
export function formatVersionSpeciesPricingSummary(rows: VersionSpeciesPriceRow[]): string {
  if (!rows.length) return '—';
  const fmt = (n: number) =>
    n.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  return rows
    .map((r) => `${r.speciesName}${r.isActive ? ' (active)' : ''}: ${fmt(r.billableTotal)}`)
    .join(' · ');
}
